import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import JSZip from "jszip";
import { StatusBadge } from "@/components/status-badge";
import { DocumentLimitBanner, NewDocumentButton } from "@/components/document-limit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { PdfPreview } from "@/components/PdfPreview";
import { AppLayout } from "@/components/app-layout";
import { FileText, Plus, Trash2, Archive, Search, Upload, Grid3X3, List, Clock, CheckCircle, XCircle, Eye, RefreshCw, Loader2, UserPlus, Shield, Info, RotateCcw, AlertTriangle, Building2, Import, FileArchive, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";
import { invalidateDocumentQueries, isFinalStatus, LIVE_QUERY_OPTIONS, pollWhileListPending } from "@/lib/live-documents";
import { ListPager, fetchPage, rememberPageSize, storedPageSize } from "@/components/list-pager";
import { importApi } from "@/lib/import-client";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface DocumentWithSigners {
  id: string;
  name: string;
  type: string;
  status: string;
  fileUrl: string;
  previewUrl: string | null;
  fileSize: number;
  createdAt: string;
  signers: Array<{
    id: string;
    recipientName: string;
    recipientEmail: string;
    status: string;
    signedAt: string | null;
    viewedAt?: string | null;
    sentAt?: string | null;
    signatureMethod?: string | null;
    declineReason?: string | null;
    /**
     * URL pointing to the fully signed PDF for this signer (or the document if all
     * signers have signed).  May be null when the document is pending.
     */
    signedDocumentUrl?: string | null;
  }>;
  crmSource?: string | null;
  crmEntityId?: string | null;
  crmEntityName?: string | null;
  crmRecordUrl?: string | null;
  crmRecordName?: string | null;
  isGovBrFlow?: boolean;
}

function formatRelativeTime(date: string | null | undefined): string {
  if (!date) return "";
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString(navigator.language?.startsWith("pt") ? "pt-BR" : "en-US");
}

function formatDetailedTime(date: string | null | undefined): string {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default function Documents() {
  const [, setLocation] = useLocation();
  const searchParams = useSearch();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    const stored = localStorage.getItem("documentsViewMode");
    return (stored === "list" || stored === "grid") ? stored : "list";
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedDoc, setSelectedDoc] = useState<DocumentWithSigners | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [showAddSigner, setShowAddSigner] = useState(false);
  const [newSignerName, setNewSignerName] = useState("");
  const [newSignerEmail, setNewSignerEmail] = useState("");
  const [isAddingSigner, setIsAddingSigner] = useState(false);
  const [activeTab, setActiveTab] = useState<"documents" | "trash">("documents");
  // Paged in the API (it used to return only the 20 newest, and the search looked only at those).
  const importFilter = new URLSearchParams(searchParams).get("import");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => storedPageSize("documentsPageSize"));
  const [trashPage, setTrashPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setPage(1);
    setTrashPage(1);
    setSelectedIds(new Set());
  }, [query, importFilter, pageSize]);

  const handleViewModeChange = (mode: "grid" | "list") => {
    setViewMode(mode);
    localStorage.setItem("documentsViewMode", mode);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (!token) {
      setLocation("/login");
      return;
    }
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [setLocation]);

  const listParams = (p: number, size: number) => {
    const params = new URLSearchParams({ page: String(p), pageSize: String(size), includeTotal: "true" });
    if (query) params.set("search", query);
    return params;
  };

  const { data: listData, isLoading, isPlaceholderData } = useQuery<{ items: any[]; total: number }>({
    queryKey: ["/api/documents", "list", { page, pageSize, query, importFilter }],
    queryFn: () => {
      const params = listParams(page, pageSize);
      if (importFilter) params.set("importId", importFilter);
      return fetchPage(getApiUrl(`/api/documents?${params}`), getAuthHeaders());
    },
    placeholderData: keepPreviousData,
    ...LIVE_QUERY_OPTIONS,
    // Keeps statuses current while any document is still waiting for a signature.
    refetchInterval: pollWhileListPending,
  });
  const documents = listData?.items;
  const documentsTotal = listData?.total ?? 0;

  const { data: importInfo } = useQuery({
    queryKey: ["/api/documents/imports", importFilter],
    queryFn: () => importApi.get(importFilter!).then((r) => r.import),
    enabled: !!importFilter,
  });

  // Trash/restore feel instant: drop the row from the cached lists first, then let the server confirm.
  const removeFromCache = async (key: string[], ids: string[]) => {
    await queryClient.cancelQueries({ queryKey: key }); // an in-flight poll must not re-add the row
    queryClient.setQueriesData({ queryKey: key }, (old: any) =>
      Array.isArray(old) ? old.filter((d: any) => !ids.includes(d.id))
        : Array.isArray(old?.items) ? { ...old, items: old.items.filter((d: any) => !ids.includes(d.id)), total: Math.max(0, old.total - ids.length) }
        : old);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(getApiUrl(`/api/documents/${id}`), { method: "DELETE", headers: getAuthHeaders() });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onMutate: (id) => removeFromCache(["/api/documents"], [id]),
    onSuccess: () => {
      toast({ title: t("docs.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
    onSettled: () => invalidateDocumentQueries(queryClient),
  });

  const deleteMultipleMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.all(ids.map(id =>
        fetch(getApiUrl(`/api/documents/${id}`), { method: "DELETE", headers: getAuthHeaders() })
      ));
      if (results.some((r) => !r.ok)) throw new Error("Failed to delete some documents");
    },
    onMutate: (ids) => removeFromCache(["/api/documents"], ids),
    onSuccess: () => {
      setSelectedIds(new Set());
      toast({ title: t("docs.deletedMultiple") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
    onSettled: () => invalidateDocumentQueries(queryClient),
  });

  const { data: trashData, isLoading: trashLoading } = useQuery<{ items: any[]; total: number }>({
    queryKey: ["/api/documents/trash", { page: trashPage, pageSize, query }],
    queryFn: () => fetchPage(getApiUrl(`/api/documents/trash?${listParams(trashPage, pageSize)}`), getAuthHeaders()),
    enabled: activeTab === "trash",
    placeholderData: keepPreviousData,
    ...LIVE_QUERY_OPTIONS,
  });
  const trashDocuments = trashData?.items;
  const trashTotal = trashData?.total ?? 0;

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(getApiUrl(`/api/documents/${id}/restore`), { 
        method: "POST", 
        headers: getAuthHeaders() 
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to restore");
      }
      return res.json();
    },
    onMutate: (id) => removeFromCache(["/api/documents/trash"], [id]),
    onSuccess: () => {
      toast({ title: t("docs.restored") || "Document restored" });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
    onSettled: () => invalidateDocumentQueries(queryClient),
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(getApiUrl(`/api/documents/${id}/permanent`), { 
        method: "DELETE", 
        headers: getAuthHeaders() 
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to permanently delete");
      }
    },
    onMutate: (id) => removeFromCache(["/api/documents/trash"], [id]),
    onSuccess: () => {
      toast({ title: t("docs.permanentlyDeleted") || "Permanently deleted" });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
    onSettled: () => invalidateDocumentQueries(queryClient),
  });

  const addSignerMutation = useMutation({
    mutationFn: async ({ documentId, name, email }: { documentId: string; name: string; email: string }) => {
      // Use absolute API URL to ensure the request goes to the backend and not the Vite dev server
      const res = await fetch(
        getApiUrl(`/api/documents/${documentId}/signers`),
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ recipientName: name, recipientEmail: email }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to add signer");
      }
      return res.json();
    },
    onSuccess: async () => {
      invalidateDocumentQueries(queryClient);
      toast({ title: t("signatures.signerAdded") });
      setShowAddSigner(false);
      setNewSignerName("");
      setNewSignerEmail("");
      setIsAddingSigner(false);
      if (selectedDoc) {
        const details = await fetchDocumentDetails(selectedDoc.id);
        setSelectedDoc(details);
      }
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      setIsAddingSigner(false);
    },
  });

  const handleAddSigner = () => {
    if (!selectedDoc || !newSignerName.trim() || !newSignerEmail.trim()) return;
    setIsAddingSigner(true);
    addSignerMutation.mutate({ documentId: selectedDoc.id, name: newSignerName, email: newSignerEmail });
  };

  const fetchDocumentDetails = async (docId: string) => {
    // Always call the backend via getApiUrl so it targets the correct API port
    const res = await fetch(getApiUrl(`/api/documents/${docId}/details`), { headers: getAuthHeaders() });
    if (!res.ok) throw new Error("Failed to fetch document details");
    return res.json();
  };

  const handleReplaceDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDoc) return;
    
    setIsReplacing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(
        getApiUrl(`/api/documents/${selectedDoc.id}/replace`),
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: formData,
        }
      );
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to replace document");
      }
      
      const result = await res.json();
      toast({ title: t("docs.replaced") });
      invalidateDocumentQueries(queryClient);
      
      // Refresh the details
      const details = await fetchDocumentDetails(selectedDoc.id);
      setSelectedDoc(details);
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setIsReplacing(false);
      if (replaceInputRef.current) {
        replaceInputRef.current.value = "";
      }
    }
  };

  const canReplaceDocument = (doc: DocumentWithSigners | null): boolean => {
    if (!doc || !doc.signers) return true;
    return !doc.signers.some(s => s.status === "signed");
  };

  const handleDocumentClick = async (doc: any) => {
    try {
      const details = await fetchDocumentDetails(doc.id);
      setSelectedDoc(details);
      setDetailsOpen(true);
    } catch (error) {
      toast({ title: t("docs.loadError"), variant: "destructive" });
    }
  };

  // ?doc=<id> (links from notifications) opens the document even when it is not on the first page.
  useEffect(() => {
    const docId = new URLSearchParams(searchParams).get("doc");
    if (!docId || !user) return;
    handleDocumentClick({ id: docId });
    setLocation(importFilter ? `/documents?import=${importFilter}` : "/documents", { replace: true });
  }, [searchParams, user]);

  // A page emptied by deletions falls back to the last page that still has rows.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(documentsTotal / pageSize));
    if (listData && !isPlaceholderData && page > last) setPage(last);
  }, [listData, isPlaceholderData, documentsTotal, pageSize, page]);

  const filteredDocs = documents || [];
  const changePage = (p: number) => {
    setPage(p);
    setSelectedIds(new Set());
  };
  const changePageSize = (size: number) => {
    setPageSize(size);
    rememberPageSize("documentsPageSize", size);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Bulk ZIP: signed PDF when it exists (else the original), plus the audit PDF when asked.
  const [zipBusy, setZipBusy] = useState(false);
  const [zipWithAudit, setZipWithAudit] = useState(true);
  const downloadZip = async () => {
    const docs = filteredDocs.filter((d: DocumentWithSigners) => selectedIds.has(d.id));
    if (docs.length === 0) return;
    setZipBusy(true);
    const zip = new JSZip();
    const auth = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
    const safe = (n: string) => n.replace(/[\\/:*?"<>|]+/g, "_").replace(/\.pdf$/i, "");
    let failed = 0;
    for (const d of docs) {
      const base = safe(d.name || d.id);
      try {
        if (zipWithAudit) {
          // Signed PDF with the audit trail appended as the last page (same file as "Download with audit")
          const a = await fetch(getApiUrl(`/api/documents/${d.id}/audit/pdf`), { headers: auth });
          if (!a.ok) throw new Error();
          zip.file(`${base}_${t("docs.zipAuditSuffix")}.pdf`, await a.blob());
        } else {
          const url = (d as any).signedFileUrl || d.fileUrl;
          const r = await fetch(url.startsWith("http") ? url : getApiUrl(url), { headers: auth });
          if (!r.ok) throw new Error();
          zip.file(`${base}.pdf`, await r.blob());
        }
      } catch { failed++; }
    }
    const blob = await zip.generateAsync({ type: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fillsign_${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
    setZipBusy(false);
    toast({ title: t("docs.zipReady"), description: failed ? `${failed} ${t("docs.zipFailed")}` : `${docs.length} ${t("docs.zipCount")}` });
  };

  const selectAll = () => {
    if (selectedIds.size === filteredDocs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocs.map((d: any) => d.id)));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "signed": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "declined": return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => <StatusBadge status={status} />;

  const hasGovBrSigners = (doc: DocumentWithSigners): boolean => {
    return doc.isGovBrFlow || doc.signers?.some(s => s.signatureMethod === "govbr") || false;
  };

  const getGovBrBadge = (doc: DocumentWithSigners) => {
    if (!hasGovBrSigners(doc)) return null;
    const isFlow = doc.isGovBrFlow;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`text-xs cursor-help ${isFlow ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300" : "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"}`}
            data-testid={`badge-doc-govbr-${doc.id}`}
          >
            <Shield className="h-3 w-3 mr-1" />
            {isFlow ? "GOV.BR" : "Nome+CPF"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs text-xs">
            {isFlow
              ? (language === "pt" ? "Fluxo GOV.BR ativo — signatários assinarão no portal gov.br com certificado ICP-Brasil" : "GOV.BR flow active — signers will sign on gov.br portal with ICP-Brasil certificate")
              : (language === "pt" ? "Assinatura verificada com Nome e CPF" : "Signature verified with Name and ID")}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  };

  if (!user) return null;

  const filteredTrashDocs = trashDocuments || [];

  const formatDaysAgo = (date: string | null) => {
    if (!date) return "-";
    const now = new Date();
    const then = new Date(date);
    const diffDays = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
    
    // Format the actual date
    const dateStr = then.toLocaleDateString(language === "pt" ? "pt-BR" : "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
    
    if (diffDays === 0) return `${dateStr} (${language === "pt" ? "Hoje" : "Today"})`;
    if (diffDays === 1) return `${dateStr} (${language === "pt" ? "Ontem" : "Yesterday"})`;
    return `${dateStr} (${diffDays} ${language === "pt" ? "dias atrás" : "days ago"})`;
  };

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/documents">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold">{t("docs.title")}</h1>
            <p className="text-muted-foreground">{t("docs.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link href="/documents/import">
              <Button variant="outline" data-testid="button-import">
                <Import className="mr-2 h-4 w-4" />
                {t("docs.import")}
              </Button>
            </Link>
            <NewDocumentButton icon={<Upload className="mr-2 h-4 w-4" />} testId="button-upload" data-guide="new-document">
              {t("docs.upload")}
            </NewDocumentButton>
          </div>
        </div>

        <DocumentLimitBanner className="mb-6" />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "documents" | "trash")} className="mb-6">
          <TabsList>
            <TabsTrigger value="documents" data-testid="tab-documents">
              <FileText className="h-4 w-4 mr-2" />
              {t("docs.allDocuments") || "All Documents"}
              {documentsTotal > 0 && (
                <Badge variant="secondary" className="ml-2">{documentsTotal.toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="trash" data-testid="tab-trash">
              <Trash2 className="h-4 w-4 mr-2" />
              {t("docs.trash") || "Trash"}
              {trashTotal > 0 && (
                <Badge variant="secondary" className="ml-2">{trashTotal.toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("docs.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="input-search"
            />
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={downloadZip} disabled={zipBusy} data-testid="button-download-zip">
                  {zipBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Archive className="mr-2 h-4 w-4" />}
                  {t("docs.downloadZip")} ({selectedIds.size})
                </Button>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Checkbox checked={zipWithAudit} onCheckedChange={(v) => setZipWithAudit(!!v)} data-testid="checkbox-zip-audit" />
                  {t("docs.zipIncludeAudit")}
                </label>
              </>
            )}
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => deleteMultipleMutation.mutate(Array.from(selectedIds))}
                disabled={deleteMultipleMutation.isPending}
                data-testid="button-delete-selected"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("docs.deleteSelected")} ({selectedIds.size})
              </Button>
            )}
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => handleViewModeChange("grid")}
                data-testid="button-view-grid"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => handleViewModeChange("list")}
                data-testid="button-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {importFilter && activeTab === "documents" && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="import-filter">
            <Badge variant="secondary" className="gap-1.5 py-1 text-sm font-normal">
              <FileArchive className="h-3.5 w-3.5" />
              {t("docs.importFilter")}: <span className="font-medium">{importInfo?.name ?? "…"}</span>
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setLocation("/documents")} data-testid="button-clear-import-filter">
              <X className="mr-1 h-4 w-4" />
              {t("docs.clearFilter")}
            </Button>
          </div>
        )}

        {activeTab === "documents" && (
          <div className={isPlaceholderData ? "opacity-60 transition-opacity" : "transition-opacity"}>
          {isLoading ? (
            <div className={viewMode === "grid" ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3" : "space-y-2"}>
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-3 sm:p-6">
                    <div className="h-12 w-12 bg-muted rounded mb-4" />
                    <div className="h-4 w-3/4 bg-muted rounded mb-2" />
                    <div className="h-3 w-1/2 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredDocs.length === 0 && (query || importFilter) ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium" data-testid="text-no-match">{t("docs.noMatch")}</h3>
              </CardContent>
            </Card>
          ) : filteredDocs.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">{t("docs.empty")}</h3>
                <p className="text-muted-foreground mb-4">{t("docs.emptyDesc")}</p>
                <NewDocumentButton>{t("docs.upload")}</NewDocumentButton>
              </CardContent>
            </Card>
          ) : viewMode === "grid" ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredDocs.map((doc: any) => (
              <Card 
                key={doc.id} 
                className="hover-elevate cursor-pointer relative" 
                data-testid={`card-document-${doc.id}`}
              >
                <CardContent className="p-3 sm:p-6">
                  <div className="absolute top-4 left-4" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(doc.id)}
                      onCheckedChange={() => toggleSelect(doc.id)}
                      data-testid={`checkbox-${doc.id}`}
                    />
                  </div>
                  <div 
                    className="flex items-start justify-between gap-4 mb-4 pl-8"
                    onClick={() => handleDocumentClick(doc)}
                  >
                    <FileText className="h-12 w-12 text-primary shrink-0" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(doc.id);
                      }}
                      data-testid={`button-delete-${doc.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div onClick={() => handleDocumentClick(doc)}>
                    <h3 className="font-medium truncate mb-1 flex items-center gap-1">{doc.name} {doc.crmSource && <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-1 bg-green-50 text-green-700 border-green-200 shrink-0"><Building2 className="h-2.5 w-2.5 mr-0.5 inline" />D365</Badge>}</h3>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-muted-foreground">{new Date(doc.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US")}</p>
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        {getGovBrBadge(doc)}
                        {getStatusBadge(doc.status)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-4 px-4 py-2 text-sm text-muted-foreground border-b">
              <Checkbox
                checked={selectedIds.size === filteredDocs.length && filteredDocs.length > 0}
                onCheckedChange={selectAll}
                data-testid="checkbox-select-all"
              />
              <span className="flex-1">{t("docs.name")}</span>
              <span className="hidden sm:block w-32 text-center">{t("docs.status")}</span>
              <span className="hidden sm:block w-32 text-center">{t("docs.date")}</span>
              <span className="hidden sm:block w-20">{t("docs.actions")}</span>
            </div>
            {/* Phones: name + actions on the first line, status and date on a second line (fixed columns left no room for the name). */}
            {filteredDocs.map((doc: any) => (
              <div 
                key={doc.id}
                className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 px-4 py-3 rounded-md border hover-elevate cursor-pointer"
                data-testid={`row-document-${doc.id}`}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(doc.id)}
                    onCheckedChange={() => toggleSelect(doc.id)}
                    data-testid={`checkbox-${doc.id}`}
                  />
                </div>
                <div className="flex items-center gap-3 flex-1 min-w-0" onClick={() => handleDocumentClick(doc)}>
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <span className="font-medium truncate">{doc.name}</span>
                              {doc.crmSource && <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-1 bg-green-50 text-green-700 border-green-200 shrink-0"><Building2 className="h-2.5 w-2.5 mr-0.5 inline" />D365</Badge>}
                </div>
                <div className="order-last basis-full pl-8 flex items-center gap-2 sm:order-none sm:basis-auto sm:pl-0 sm:w-32 sm:justify-center sm:gap-1" onClick={() => handleDocumentClick(doc)}>
                  {getGovBrBadge(doc)}
                  {getStatusBadge(doc.status)}
                  <span className="sm:hidden ml-auto text-sm text-muted-foreground">
                    {new Date(doc.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US")}
                  </span>
                </div>
                <div className="hidden sm:block w-32 text-center text-sm text-muted-foreground" onClick={() => handleDocumentClick(doc)}>
                  {new Date(doc.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US")}
                </div>
                <div className="w-20 shrink-0 flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDocumentClick(doc)}
                    data-testid={`button-view-${doc.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(doc.id);
                    }}
                    data-testid={`button-delete-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
          <ListPager page={page} pageSize={pageSize} total={documentsTotal} onPage={changePage} onPageSize={changePageSize} testId="documents-pager" />
          </div>
        )}

        {activeTab === "trash" && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">{language === "pt" ? "Documentos na lixeira" : "Documents in trash"}</p>
                <p className="text-amber-700 dark:text-amber-300">{language === "pt" ? "Documentos excluídos ficam na lixeira por 30 dias. Após esse período, são removidos permanentemente e não podem ser recuperados." : "Deleted documents stay in trash for 30 days. After that, they are permanently removed and cannot be recovered."}</p>
              </div>
            </div>
            {trashLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="h-4 w-3/4 bg-muted rounded mb-2" />
                      <div className="h-3 w-1/2 bg-muted rounded" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredTrashDocs.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Trash2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-medium mb-2">{t("docs.trashEmpty") || "Trash is empty"}</h3>
                  <p className="text-muted-foreground">{t("docs.trashEmptyDesc") || "Deleted documents will appear here"}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-4 px-4 py-2 text-sm text-muted-foreground border-b">
                  <span className="flex-1">{t("docs.name")}</span>
                  <span className="w-32 text-center">{t("docs.deletedDate") || "Deleted"}</span>
                  <span className="w-40 text-center">{t("docs.actions")}</span>
                </div>
                {filteredTrashDocs.map((doc: any) => (
                  <div 
                    key={doc.id}
                    className="flex items-center gap-4 px-4 py-3 rounded-md border"
                    data-testid={`row-trash-${doc.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div>
                        <span className="font-medium truncate flex items-center gap-1">{doc.name} {doc.crmSource && <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-1 bg-green-50 text-green-700 border-green-200 shrink-0"><Building2 className="h-2.5 w-2.5 mr-0.5 inline" />D365</Badge>}</span>
                        {doc.deleteReason && (
                          <span className="text-xs text-muted-foreground">{doc.deleteReason}</span>
                        )}
                      </div>
                    </div>
                    <div className="w-32 text-center text-sm text-muted-foreground">
                      {formatDaysAgo(doc.deletedAt)}
                    </div>
                    <div className="w-40 flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreMutation.mutate(doc.id)}
                        disabled={restoreMutation.isPending}
                        data-testid={`button-restore-${doc.id}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        {t("docs.restore") || "Restore"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm(t("docs.permanentDeleteConfirm") || "Permanently delete this document? This cannot be undone.")) {
                            permanentDeleteMutation.mutate(doc.id);
                          }
                        }}
                        disabled={permanentDeleteMutation.isPending}
                        data-testid={`button-permanent-delete-${doc.id}`}
                      >
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
                <ListPager page={trashPage} pageSize={pageSize} total={trashTotal} onPage={setTrashPage} onPageSize={changePageSize} testId="trash-pager" />
                <div className="p-4 bg-muted/50 rounded-md text-sm text-muted-foreground flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  {t("docs.retentionNote") || "Documents in trash are automatically deleted after the retention period."}
                </div>
              </div>
            )}
          </div>
        )}

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedDoc?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="min-w-0 space-y-6 pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t("docs.type")}</p>
                  <p className="font-medium">{selectedDoc.type.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("docs.status")}</p>
                  {getStatusBadge(selectedDoc.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("docs.size")}</p>
                  <p className="font-medium">{(selectedDoc.fileSize / 1024).toFixed(1)} KB</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("docs.created")}</p>
                  <p className="font-medium">{new Date(selectedDoc.createdAt).toLocaleDateString(language === "pt" ? "pt-BR" : "en-US")}</p>
                </div>
              </div>

              {selectedDoc && (
                (() => {
                  // Determine the best preview source: prefer signed document for any signer
                  const signerSigned = selectedDoc.signers?.find(s => s.signedDocumentUrl);
                  const preview = signerSigned?.signedDocumentUrl || selectedDoc.previewUrl;
                  if (!preview) return null;
                  return (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <h3 className="font-medium">{t("docs.preview")}</h3>
                        <Link href={`/documents/${selectedDoc.id}`}>
                          <Button variant="outline" size="sm" data-testid="button-open-document-page">
                            <Eye className="mr-2 h-4 w-4" /> {language === "pt" ? "Abrir página do documento" : "Open document page"}
                          </Button>
                        </Link>
                      </div>
                      <PdfPreview src={preview} className="w-full" maxHeight={Math.max(360, Math.round(window.innerHeight * 0.6))} />
                    </div>
                  );
                })()
              )}

              <div>
                <h3 className="font-medium mb-3">{t("docs.signers")}</h3>
                {selectedDoc.signers && selectedDoc.signers.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDoc.signers.map((signer) => (
                      <div key={signer.id} className="p-3 rounded-md border space-y-2" data-testid={`signer-row-${signer.id}`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(signer.status)}
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{signer.recipientName}</p>
                                {signer.signatureMethod === "govbr" && (
                                  <Badge 
                                    variant="outline" 
                                    className="text-xs bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                                  >
                                    <Shield className="h-3 w-3 mr-1" />
                                    GOV.BR
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{signer.recipientEmail}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {signer.sentAt && (
                                <span className="text-xs text-muted-foreground" title={formatDetailedTime(signer.sentAt)}>
                                  Sent {formatRelativeTime(signer.sentAt)}
                                </span>
                              )}
                              {signer.viewedAt && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1" title={formatDetailedTime(signer.viewedAt)}>
                                  <Eye className="h-3 w-3" />
                                  Viewed {formatRelativeTime(signer.viewedAt)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <StatusBadge status={signer.status} />
                          {signer.signedAt && (
                            <p className="text-xs text-muted-foreground mt-1" title={formatDetailedTime(signer.signedAt)}>
                              Signed {formatRelativeTime(signer.signedAt)}
                            </p>
                          )}
                        </div>
                        </div>
                        {signer.signatureMethod === "govbr" && signer.status === "pending" && (
                          <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <div className="flex items-start gap-2">
                              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800 dark:text-amber-200">
                                <p className="font-medium">{t("ui.manual_workflow_required")}</p>
                                <p>{t("docs.govbrManualHint")}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t("docs.noSigners")}</p>
                )}

                {!isFinalStatus(selectedDoc.status) && (
                  <div className="mt-4">
                    {showAddSigner ? (
                      <div className="space-y-3 p-4 rounded-md border bg-muted/30">
                        <div className="space-y-2">
                          <Label htmlFor="signer-name">{t("signatures.recipientName")}</Label>
                          <Input
                            id="signer-name"
                            placeholder={t("docs.signerNamePlaceholder")}
                            value={newSignerName}
                            onChange={(e) => setNewSignerName(e.target.value)}
                            data-testid="input-new-signer-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signer-email">{t("signatures.recipientEmail")}</Label>
                          <Input
                            id="signer-email"
                            type="email"
                            placeholder="john@example.com"
                            value={newSignerEmail}
                            onChange={(e) => setNewSignerEmail(e.target.value)}
                            data-testid="input-new-signer-email"
                          />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            onClick={handleAddSigner}
                            disabled={isAddingSigner || !newSignerName.trim() || !newSignerEmail.trim()}
                            data-testid="button-confirm-add-signer"
                          >
                            {isAddingSigner ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="mr-2 h-4 w-4" />
                            )}
                            {t("signatures.addSigner")}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setShowAddSigner(false);
                              setNewSignerName("");
                              setNewSignerEmail("");
                            }}
                            data-testid="button-cancel-add-signer"
                          >
                            {t("common.cancel")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setShowAddSigner(true)}
                        data-testid="button-add-signer"
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        {t("signatures.addSigner")}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t flex-wrap">
                <input
                  type="file"
                  ref={replaceInputRef}
                  onChange={handleReplaceDocument}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                />
                {canReplaceDocument(selectedDoc) && (
                  <Button 
                    variant="outline" 
                    onClick={() => replaceInputRef.current?.click()}
                    disabled={isReplacing}
                    data-testid="button-replace-document"
                  >
                    {isReplacing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    {t("docs.replace")}
                  </Button>
                )}
                <a href={selectedDoc.fileUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">{t("docs.download")}</Button>
                </a>
                <Link href={`/documents/${selectedDoc.id}`}>
                  <Button>{t("docs.viewFull")}</Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
